/**
 * Global table enhancer: adds sortable + filterable column headers to every
 * <table class="data-table"> in the app, with persistence in localStorage.
 *
 * Behaviour:
 *  - Single click on header: toggles sort (asc -> desc -> none).
 *  - Click on filter icon (right side of header): opens checkbox popup with
 *    unique column values (Excel-like) + search input.
 *  - Long-press (mobile): opens filter popup.
 *  - Persists per (route + table_signature + column_index) key in localStorage.
 *
 * Opt-out: add `data-no-enhance="true"` on the <table> or `data-skip-filter="true"`
 * on individual <th>.
 */

const STORAGE_PREFIX = 'tableEnh:v1:';
const ENHANCED_FLAG = 'tablefilterEnhanced';

const ICON_SORT_NEUTRAL = '\u21C5'; // ⇅
const ICON_SORT_ASC = '\u2191';     // ↑
const ICON_SORT_DESC = '\u2193';    // ↓
const ICON_FILTER = '\u25BE';       // ▾

function loadState(key) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    return raw ? JSON.parse(raw) : { sortCol: null, sortDir: null, filters: {} };
  } catch {
    return { sortCol: null, sortDir: null, filters: {} };
  }
}

function saveState(key, state) {
  try {
    const hasSort = state.sortCol != null && state.sortDir;
    const hasFilters = Object.keys(state.filters || {}).some(k => (state.filters[k] || []).length > 0);
    if (!hasSort && !hasFilters) localStorage.removeItem(STORAGE_PREFIX + key);
    else localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(state));
  } catch {
    /* quota / private mode - ignore */
  }
}

function tableSignature(table) {
  // Sign by path + position-amongst-tables + thead headers labels (stable for the page)
  const tables = document.querySelectorAll('table.data-table');
  const idx = Array.prototype.indexOf.call(tables, table);
  const headerLabels = Array.from(table.querySelectorAll('thead th'))
    .map(th => (th.textContent || '').trim().slice(0, 40))
    .join('|');
  return `${window.location.pathname}::${idx}::${headerLabels}`;
}

function getCellText(cell) {
  if (!cell) return '';
  // Prefer explicit data-sort-value for custom rows
  const explicit = cell.getAttribute('data-sort-value');
  if (explicit != null) return explicit;
  return (cell.textContent || '').trim();
}

function smartCompare(a, b) {
  // Try number
  const na = parseFloat(String(a).replace(/\s+/g, '').replace(',', '.').replace(/[^\d.\-]/g, ''));
  const nb = parseFloat(String(b).replace(/\s+/g, '').replace(',', '.').replace(/[^\d.\-]/g, ''));
  const isNum = Number.isFinite(na) && Number.isFinite(nb)
    && /\d/.test(String(a)) && /\d/.test(String(b));
  if (isNum) return na - nb;
  // Try date dd-mm-yyyy or dd/mm/yyyy
  const reD = /^(\d{2})[-\/](\d{2})[-\/](\d{4})$/;
  const ma = reD.exec(String(a).trim());
  const mb = reD.exec(String(b).trim());
  if (ma && mb) {
    const da = new Date(`${ma[3]}-${ma[2]}-${ma[1]}`).getTime();
    const db = new Date(`${mb[3]}-${mb[2]}-${mb[1]}`).getTime();
    if (!isNaN(da) && !isNaN(db)) return da - db;
  }
  // Fallback: locale-aware string compare
  return String(a).localeCompare(String(b), 'fr', { sensitivity: 'base', numeric: true });
}

function ensureFilterPopupHost() {
  let host = document.getElementById('table-enh-popup-host');
  if (!host) {
    host = document.createElement('div');
    host.id = 'table-enh-popup-host';
    host.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:99999;';
    document.body.appendChild(host);
  }
  return host;
}

let _activePopup = null;
function closePopup() {
  if (_activePopup) {
    _activePopup.remove();
    _activePopup = null;
  }
}
document.addEventListener('mousedown', (e) => {
  if (_activePopup && !_activePopup.contains(e.target)) closePopup();
}, true);

function openFilterPopup(table, colIdx, anchorEl, state, applyState) {
  closePopup();
  const host = ensureFilterPopupHost();
  const rows = Array.from(table.querySelectorAll('tbody tr'));
  // Build unique values for the column (only from rows matching OTHER filters)
  const uniqueSet = new Map(); // value -> count
  rows.forEach(r => {
    let matchOthers = true;
    Object.entries(state.filters || {}).forEach(([k, allowed]) => {
      if (Number(k) === colIdx) return;
      if (!allowed || !allowed.length) return;
      const t = getCellText(r.children[Number(k)]);
      if (!allowed.includes(t)) matchOthers = false;
    });
    if (!matchOthers) return;
    const v = getCellText(r.children[colIdx]);
    uniqueSet.set(v, (uniqueSet.get(v) || 0) + 1);
  });
  const values = Array.from(uniqueSet.keys()).sort((a, b) => smartCompare(a, b));
  const currentAllowed = state.filters[colIdx] || null; // null = all

  const rect = anchorEl.getBoundingClientRect();
  const pop = document.createElement('div');
  pop.className = 'table-enh-popup';
  const top = Math.min(rect.bottom + 4, window.innerHeight - 360);
  const left = Math.min(rect.left, window.innerWidth - 280);
  pop.style.cssText = `
    position:fixed;top:${top}px;left:${left}px;width:260px;max-height:340px;
    background:white;border:1px solid #E2E2E6;border-radius:10px;
    box-shadow:0 8px 24px rgba(0,0,0,.12);pointer-events:auto;
    display:flex;flex-direction:column;font-family:inherit;font-size:13px;color:#202124;
    overflow:hidden;`;
  pop.innerHTML = `
    <div style="padding:10px 12px;border-bottom:1px solid #EEE;display:flex;align-items:center;gap:6px;">
      <input type="text" data-role="search" placeholder="Rechercher..." style="flex:1;padding:6px 8px;border:1px solid #E2E2E6;border-radius:6px;font-size:13px;outline:none;" />
    </div>
    <div style="padding:6px 12px;border-bottom:1px solid #EEE;display:flex;justify-content:space-between;align-items:center;">
      <button data-role="select-all" style="border:none;background:transparent;color:#1565C0;font-size:12px;cursor:pointer;font-weight:600;">Tout selectionner</button>
      <button data-role="clear" style="border:none;background:transparent;color:#5E5F66;font-size:12px;cursor:pointer;">Effacer</button>
    </div>
    <div data-role="list" style="flex:1;overflow-y:auto;padding:4px 6px;"></div>
    <div style="padding:8px 12px;border-top:1px solid #EEE;display:flex;gap:8px;justify-content:flex-end;">
      <button data-role="cancel" style="padding:6px 12px;border:1px solid #E2E2E6;background:white;border-radius:6px;cursor:pointer;font-size:12px;">Annuler</button>
      <button data-role="apply" style="padding:6px 14px;border:none;background:#002FA7;color:white;border-radius:6px;cursor:pointer;font-size:12px;font-weight:600;">Appliquer</button>
    </div>
  `;
  host.appendChild(pop);
  _activePopup = pop;

  const list = pop.querySelector('[data-role="list"]');
  const search = pop.querySelector('[data-role="search"]');

  // Working selection (mutated until "Apply")
  let selected = currentAllowed ? new Set(currentAllowed) : new Set(values); // null => all selected

  function renderList(filterText) {
    const ft = (filterText || '').toLowerCase();
    list.innerHTML = '';
    const filteredVals = values.filter(v => v.toLowerCase().includes(ft));
    if (!filteredVals.length) {
      list.innerHTML = '<div style="padding:12px;color:#9CA3AF;text-align:center;font-size:12px;">Aucune valeur</div>';
      return;
    }
    filteredVals.forEach(v => {
      const id = `tef-${Math.random().toString(36).slice(2, 8)}`;
      const row = document.createElement('label');
      row.htmlFor = id;
      row.style.cssText = 'display:flex;align-items:center;gap:8px;padding:5px 6px;border-radius:6px;cursor:pointer;';
      row.addEventListener('mouseenter', () => row.style.backgroundColor = '#F4F5F7');
      row.addEventListener('mouseleave', () => row.style.backgroundColor = 'transparent');
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.id = id;
      cb.checked = selected.has(v);
      cb.addEventListener('change', () => {
        if (cb.checked) selected.add(v); else selected.delete(v);
      });
      const lbl = document.createElement('span');
      lbl.textContent = v === '' ? '(vide)' : v;
      lbl.style.cssText = 'flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
      const cnt = document.createElement('span');
      cnt.textContent = uniqueSet.get(v);
      cnt.style.cssText = 'color:#9CA3AF;font-size:11px;';
      row.appendChild(cb);
      row.appendChild(lbl);
      row.appendChild(cnt);
      list.appendChild(row);
    });
  }
  renderList('');
  search.addEventListener('input', e => renderList(e.target.value));
  pop.querySelector('[data-role="select-all"]').addEventListener('click', () => {
    selected = new Set(values);
    renderList(search.value);
  });
  pop.querySelector('[data-role="clear"]').addEventListener('click', () => {
    selected = new Set();
    renderList(search.value);
  });
  pop.querySelector('[data-role="cancel"]').addEventListener('click', closePopup);
  pop.querySelector('[data-role="apply"]').addEventListener('click', () => {
    // If selection covers ALL values, treat as "no filter"
    if (selected.size === values.length || selected.size === 0 && values.length === 0) {
      delete state.filters[colIdx];
    } else {
      state.filters[colIdx] = Array.from(selected);
    }
    applyState();
    closePopup();
  });
  setTimeout(() => search.focus(), 50);
}

export function enhanceDataTable(table) {
  if (!table || table.dataset[ENHANCED_FLAG] === '1') return;
  if (table.dataset.noEnhance === 'true') return;
  const thead = table.querySelector(':scope > thead');
  const tbody = table.querySelector(':scope > tbody');
  if (!thead || !tbody) return;
  // Only single-row headers are supported
  const headerRow = thead.querySelector('tr');
  if (!headerRow) return;
  const ths = Array.from(headerRow.children).filter(c => c.tagName === 'TH');
  if (!ths.length) return;

  table.dataset[ENHANCED_FLAG] = '1';
  const storageKey = tableSignature(table);
  const state = loadState(storageKey);

  function applyState() {
    const rows = Array.from(tbody.querySelectorAll(':scope > tr'));
    rows.forEach(r => {
      let visible = true;
      Object.entries(state.filters || {}).forEach(([k, allowed]) => {
        if (!allowed || !allowed.length) return;
        const t = getCellText(r.children[Number(k)]);
        if (!allowed.includes(t)) visible = false;
      });
      r.style.display = visible ? '' : 'none';
      // Mark for native filtering tools
      if (!visible) r.setAttribute('data-enh-hidden', '1');
      else r.removeAttribute('data-enh-hidden');
    });
    if (state.sortCol != null && state.sortDir) {
      const visible = rows.filter(r => r.style.display !== 'none');
      visible.sort((a, b) => {
        const av = getCellText(a.children[state.sortCol]);
        const bv = getCellText(b.children[state.sortCol]);
        return state.sortDir === 'asc' ? smartCompare(av, bv) : smartCompare(bv, av);
      });
      const frag = document.createDocumentFragment();
      visible.forEach(r => frag.appendChild(r));
      tbody.appendChild(frag);
      // Also push hidden rows to the end so they stay out of the way
      rows.filter(r => r.style.display === 'none').forEach(r => tbody.appendChild(r));
    }
    // Update icons
    ths.forEach((th, i) => {
      const sortInd = th.querySelector(':scope > .th-enh-sort');
      const filterInd = th.querySelector(':scope > .th-enh-filter');
      if (sortInd) {
        if (i === state.sortCol && state.sortDir === 'asc') { sortInd.textContent = ICON_SORT_ASC; sortInd.style.opacity = '1'; sortInd.style.color = '#002FA7'; }
        else if (i === state.sortCol && state.sortDir === 'desc') { sortInd.textContent = ICON_SORT_DESC; sortInd.style.opacity = '1'; sortInd.style.color = '#002FA7'; }
        else { sortInd.textContent = ICON_SORT_NEUTRAL; sortInd.style.opacity = '.35'; sortInd.style.color = 'inherit'; }
      }
      if (filterInd) {
        const has = (state.filters || {})[i] && (state.filters[i] || []).length > 0;
        filterInd.style.opacity = has ? '1' : '.35';
        filterInd.style.color = has ? '#DC2626' : 'inherit';
        filterInd.title = has ? `Filtre actif (${state.filters[i].length} valeur(s))` : 'Filtrer';
      }
    });
    saveState(storageKey, state);
  }

  function setupTh(th, colIdx) {
    if (th.dataset.skipFilter === 'true') return;
    if (th.querySelector(':scope > .th-enh-sort')) return; // already wired
    th.style.userSelect = 'none';
    th.style.cursor = 'pointer';
    th.style.position = th.style.position || 'relative';
    // Sort indicator
    const sortInd = document.createElement('span');
    sortInd.className = 'th-enh-sort';
    sortInd.textContent = ICON_SORT_NEUTRAL;
    sortInd.style.cssText = 'margin-left:6px;font-size:11px;opacity:.35;';
    th.appendChild(sortInd);
    // Filter button
    const filterBtn = document.createElement('span');
    filterBtn.className = 'th-enh-filter';
    filterBtn.textContent = ICON_FILTER;
    filterBtn.title = 'Filtrer';
    filterBtn.setAttribute('role', 'button');
    filterBtn.style.cssText = 'margin-left:4px;padding:0 6px;border-radius:4px;cursor:pointer;font-size:11px;opacity:.35;display:inline-block;';
    filterBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      openFilterPopup(table, colIdx, th, state, applyState);
    });
    filterBtn.addEventListener('mouseenter', () => filterBtn.style.backgroundColor = '#F4F5F7');
    filterBtn.addEventListener('mouseleave', () => filterBtn.style.backgroundColor = 'transparent');
    th.appendChild(filterBtn);

    // Click on header (excluding filter btn) -> toggle sort
    th.addEventListener('click', (e) => {
      if (filterBtn.contains(e.target)) return;
      // Don't sort if user is clicking on an interactive child (input, button, link, select)
      const t = e.target;
      if (t.closest('input,button,select,a,label')) return;
      if (state.sortCol === colIdx) {
        if (state.sortDir === 'asc') state.sortDir = 'desc';
        else if (state.sortDir === 'desc') { state.sortCol = null; state.sortDir = null; }
        else state.sortDir = 'asc';
      } else {
        state.sortCol = colIdx;
        state.sortDir = 'asc';
      }
      applyState();
    });

    // Long-press on touch devices opens filter
    let touchTimer = null;
    th.addEventListener('touchstart', (e) => {
      if (filterBtn.contains(e.target)) return;
      touchTimer = setTimeout(() => {
        touchTimer = null;
        openFilterPopup(table, colIdx, th, state, applyState);
      }, 500);
    }, { passive: true });
    const cancelTouch = () => { if (touchTimer) { clearTimeout(touchTimer); touchTimer = null; } };
    th.addEventListener('touchend', cancelTouch);
    th.addEventListener('touchmove', cancelTouch);
    th.addEventListener('touchcancel', cancelTouch);
  }

  ths.forEach((th, i) => setupTh(th, i));
  applyState();

  // Watch for tbody mutations (rows added/removed) and re-apply state
  const mo = new MutationObserver(() => {
    // Debounce
    clearTimeout(table._enhDebounce);
    table._enhDebounce = setTimeout(() => {
      // Re-attach handlers in case React replaced any th
      const newThs = Array.from(table.querySelectorAll(':scope > thead > tr > th'));
      newThs.forEach((th, i) => setupTh(th, i));
      applyState();
    }, 80);
  });
  mo.observe(tbody, { childList: true, subtree: true, characterData: true });
}

export function startGlobalTableEnhancer() {
  if (typeof window === 'undefined') return () => {};
  // Initial pass
  document.querySelectorAll('table.data-table').forEach(enhanceDataTable);

  // Observe whole document for added tables
  const docObserver = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach(n => {
        if (!(n instanceof HTMLElement)) return;
        if (n.matches?.('table.data-table')) enhanceDataTable(n);
        n.querySelectorAll?.('table.data-table').forEach(enhanceDataTable);
      });
    }
  });
  docObserver.observe(document.body, { childList: true, subtree: true });

  // Also re-scan on route changes (SPA): expose a manual rescan
  return () => {
    docObserver.disconnect();
  };
}

export function rescanTables() {
  document.querySelectorAll('table.data-table').forEach(enhanceDataTable);
}
